import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { ApiService } from 'src/app/services/api.service';
import { ApiUrl } from 'src/app/services/apiUrl';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';

/** Treat angular-editor placeholder HTML as empty (e.g. `<p><br></p>`). */
function richTextRequired(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (value == null || value === '') {
    return { required: true };
  }
  const text = String(value)
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();
  return text.length === 0 ? { required: true } : null;
}

@Component({
  selector: 'app-qr-landing-page',
  templateUrl: './qr-landing-page.component.html',
  styleUrls: ['./qr-landing-page.component.scss']
})
export class QrLandingPageComponent implements OnInit {
  isSubmitted: any;
  qrLandingForm: any;
  isLoading: boolean = false;
  isSaving: boolean = false;
  /** Language last successfully loaded — used to revert cancelled language switches. */
  private currentLanguage = 'en';
  /** Incremented on each load so stale responses cannot overwrite newer language content. */
  private loadSeq = 0;

  selectedLanguage: any = [
    { lable: 'English', value: 'en' },
    { lable: 'Danish', value: 'da' }
  ];

  editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: 'auto',
    minHeight: '200px',
    translate: 'no',
    sanitize: false
  };

  constructor(
    public service: ApiService,
    public toastr: ToastrService
  ) { }

  ngOnInit(): void {
    this.qrLandingForm = new FormGroup({
      language: new FormControl('en'),
      content: new FormControl('', [Validators.required, richTextRequired])
    });
    this.currentLanguage = this.qrLandingForm.value.language;
    this.getQrLandingPage();
  }

  onLanguageChange(): void {
    const newLang = this.qrLandingForm.value.language;
    if (newLang === this.currentLanguage) {
      return;
    }

    if (this.qrLandingForm.dirty) {
      Swal.fire({
        title: 'Unsaved changes',
        text: 'You have unsaved changes. Switch language and discard them?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Discard changes',
        cancelButtonText: 'Stay'
      }).then((result) => {
        if (result.isConfirmed) {
          this.currentLanguage = newLang;
          this.getQrLandingPage();
        } else {
          this.qrLandingForm.patchValue({ language: this.currentLanguage }, { emitEvent: false });
        }
      });
      return;
    }

    this.currentLanguage = newLang;
    this.getQrLandingPage();
  }

  getQrLandingPage(): void {
    const seq = ++this.loadSeq;
    this.isLoading = true;
    this.editorConfig = { ...this.editorConfig, editable: false };
    const lang = this.qrLandingForm.value.language;

    this.service.getRequest(`${ApiUrl.getQrLanding}${lang}`).subscribe({
      next: (res: any) => {
        if (seq !== this.loadSeq) {
          return;
        }
        this.isLoading = false;
        this.editorConfig = { ...this.editorConfig, editable: true };
        const loadedLang = res.response.data.language ? res.response.data.language : lang;
        this.qrLandingForm.patchValue({
          language: loadedLang,
          content: res.response.data.content
        });
        this.currentLanguage = loadedLang;
        this.qrLandingForm.markAsPristine();
      },
      error: (err: any) => {
        if (seq !== this.loadSeq) {
          return;
        }
        this.isLoading = false;
        this.editorConfig = { ...this.editorConfig, editable: true };

        if (err.status === 404) {
          this.qrLandingForm.patchValue({ content: '' });
          this.qrLandingForm.get('content')?.markAsUntouched();
          this.currentLanguage = lang;
          this.qrLandingForm.markAsPristine();
          return;
        }

        this.toastr.error(err.error?.response?.message || 'Failed to load QR landing page content');
        this.qrLandingForm.patchValue({ language: this.currentLanguage }, { emitEvent: false });
      }
    });
  }

  onSave(): void {
    this.isSubmitted = true;
    if (this.qrLandingForm.invalid || this.isSaving) {
      return;
    }
    this.isSaving = true;
    const body = {
      language: this.qrLandingForm.value.language,
      content: this.qrLandingForm.value.content
    };
    this.service.postRequest(ApiUrl.createQrLanding, body).subscribe({
      next: (res: any) => {
        this.isSaving = false;
        this.currentLanguage = this.qrLandingForm.value.language;
        this.qrLandingForm.markAsPristine();
        this.toastr.success(res.response.message);
      },
      error: (err: any) => {
        this.isSaving = false;
        this.toastr.error(err.error?.response?.message || 'Failed to save QR landing page');
      }
    });
  }
}
